/* eslint-disable react-hooks/exhaustive-deps */
/**
 * LM Mobile · Fuel Refills (Phase 15.10.3)
 * ------------------------------------------------------
 * Mobile-first replacement for FuelAdmin in the LM Shell:
 *   - Big card per refill (driver / truck / station / amount)
 *   - Prominent "Pay Now" button when a UPI QR is available
 *   - Tap-to-zoom photo gallery (bill photos, payment proof, pump QR)
 *   - UPI intent link (`upi://pay?...`) so the LM can pay directly from their phone
 *   - Approve / Reject inline
 *   - Filter: Pending / Approved / All
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Fuel,
  RefreshCw,
  QrCode,
  Image as ImageIcon,
  Receipt,
  X,
  Check,
  IndianRupee,
  Search,
  ExternalLink,
  Download,
} from "lucide-react";

const fmtINR = (n) => "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN");

export default function LMFuelMobile() {
  const [refills, setRefills] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("pending");  // pending | approved | all
  const [viewer, setViewer] = useState(null);       // { photos: [{url,label}], idx, refill }
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/fuel/refills", { params: { limit: 200 } }),
        api.get("/fuel/stations"),
      ]);
      setRefills(r.data || []);
      setStations(s.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Lookup station data on demand (for UPI ID/QR)
  const stationMap = useMemo(() => {
    const m = {};
    stations.forEach((s) => { m[s.id] = s; });
    return m;
  }, [stations]);

  const filtered = useMemo(() => {
    let arr = refills;
    if (filter === "pending") arr = arr.filter((r) => r.status === "pending");
    else if (filter === "approved") arr = arr.filter((r) => r.status === "approved");
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) =>
        (r.truck_no || "").toLowerCase().includes(q) ||
        (r.driver_name || "").toLowerCase().includes(q) ||
        (r.station_name || "").toLowerCase().includes(q),
      );
    }
    return arr;
  }, [refills, search, filter]);

  const counts = useMemo(() => ({
    pending: refills.filter((r) => r.status === "pending").length,
    approved: refills.filter((r) => r.status === "approved").length,
    all: refills.length,
  }), [refills]);

  const totalPending = useMemo(() =>
    refills.filter((r) => r.status === "pending")
      .reduce((s, r) => s + Number(r.total_amount || 0), 0),
    [refills]);

  const act = async (id, action, reason) => {
    setBusyId(id);
    try {
      await api.post(`/fuel/refills/${id}/approve`, {
        action, reason: reason || null,
      });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="lm-fuel-mobile">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            LM · Fuel
          </div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Refills &amp; Payments
          </h1>
          {counts.pending > 0 && (
            <p className="text-[11px] text-red-700 font-semibold mt-0.5">
              {counts.pending} pending · {fmtINR(totalPending)} to pay
            </p>
          )}
        </div>
        <button
          onClick={load}
          className="rounded-lg ring-1 ring-slate-200 bg-white p-2"
          data-testid="lm-fuel-refresh"
        >
          <RefreshCw className="h-4 w-4 text-slate-600" />
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search truck / driver / station…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg ring-1 ring-slate-200 bg-white"
          data-testid="lm-fuel-search"
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
        {[
          { k: "pending", label: "Pending Payment" },
          { k: "approved", label: "Paid" },
          { k: "all", label: "All" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`lm-fuel-filter-${f.k}`}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-[11px] font-semibold ring-1 transition flex-shrink-0 ${
              filter === f.k
                ? "bg-slate-900 text-white ring-slate-900"
                : "bg-white text-slate-700 ring-slate-200"
            }`}
          >
            {f.label} <span className="opacity-70">· {counts[f.k]}</span>
          </button>
        ))}
      </div>

      {/* Refill cards */}
      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-10 bg-white rounded-xl ring-1 ring-slate-200">
          No refills match.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <RefillCard
              key={r.id}
              refill={r}
              station={stationMap[r.station_id]}
              busy={busyId === r.id}
              onView={(photos, idx) => setViewer({ photos, idx, refill: r })}
              onApprove={() => act(r.id, "approve")}
              onReject={() => {
                const reason = window.prompt("Reject reason?", "");
                if (reason === null) return;
                act(r.id, "reject", reason);
              }}
            />
          ))}
        </div>
      )}

      {viewer && (
        <PhotoViewer
          viewer={viewer}
          onClose={() => setViewer(null)}
          onPromoteQR={async () => {
            const { refill } = viewer;
            if (!window.confirm("Set this QR as station's default? Future drivers will see this QR for this pump.")) return;
            try {
              await api.post(`/fuel/refills/${refill.id}/promote-pump-qr`);
              toast.success("Promoted to station default ✓");
              setViewer(null);
              await load();
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
            }
          }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Refill Card                                                            */
/* -------------------------------------------------------------------- */
function RefillCard({ refill, station, busy, onView, onApprove, onReject }) {
  const r = refill;
  const bills = (r.bill_photos || []).filter(Boolean);
  const hasPaymentProof = !!r.payment_proof_photo;
  const hasPumpQr = !!r.pump_qr_photo;
  const stationUpiId = station?.upi_id || null;
  const stationQrPhoto = station?.upi_qr_photo || null;
  const stationPayee = station?.upi_payee_name || station?.name || r.station_name;

  // UPI intent link — opens any UPI app installed on the phone
  const upiLink = stationUpiId
    ? `upi://pay?pa=${encodeURIComponent(stationUpiId)}` +
      `&pn=${encodeURIComponent(stationPayee)}` +
      `&am=${Number(r.total_amount || 0).toFixed(2)}` +
      `&cu=INR&tn=${encodeURIComponent(`Fuel ${r.truck_no} ${r.refill_date}`)}`
    : null;

  // Build photo list for viewer
  const photoList = useMemo(() => {
    const arr = [];
    if (hasPaymentProof) arr.push({
      url: r.payment_proof_photo,
      label: "Payment Proof (driver)",
    });
    if (hasPumpQr) arr.push({
      url: r.pump_qr_photo,
      label: "Pump QR — uploaded by driver",
      promotable: true,
    });
    if (stationQrPhoto && !hasPumpQr) arr.push({
      url: stationQrPhoto,
      label: "Pump QR (station default)",
    });
    bills.forEach((p, i) => arr.push({ url: p, label: `Bill ${i + 1}` }));
    return arr;
  }, [r, bills, hasPaymentProof, hasPumpQr, stationQrPhoto]);

  const hasPayableQr = stationUpiId || stationQrPhoto || hasPumpQr;

  return (
    <div
      data-testid={`lm-fuel-card-${r.id}`}
      className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden"
    >
      {/* Top — meta */}
      <div className="px-3 pt-3 pb-2 flex items-start gap-2">
        <div className="h-10 w-10 rounded-xl bg-slate-900 text-amber-300 flex items-center justify-center flex-shrink-0">
          <Fuel className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-bold text-slate-900">{r.truck_no}</span>
            <span className="text-[10px] text-slate-500">·</span>
            <span className="text-[11px] text-slate-600 truncate">{r.driver_name}</span>
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {r.station_name || "—"} · {r.refill_date}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-base font-bold text-slate-900 flex items-center justify-end">
            <IndianRupee className="h-3.5 w-3.5" />
            {Math.round(Number(r.total_amount || 0)).toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-slate-500">{r.diesel_litres} L</div>
        </div>
      </div>

      {/* Status badge row */}
      <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
        <StatusChip status={r.status} />
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase font-semibold">
          {r.payment_mode || "—"}
        </span>
        {r.mileage_kmpl && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
            {r.mileage_kmpl} km/L
          </span>
        )}
      </div>

      {/* Photo gallery — bills + payment proof + pump QR */}
      {photoList.length > 0 && (
        <div className="px-3 pb-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
            <ImageIcon className="h-3 w-3" /> Photos ({photoList.length})
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {photoList.slice(0, 8).map((p, i) => (
              <button
                key={i}
                onClick={() => onView(photoList, i)}
                data-testid={`lm-fuel-photo-${r.id}-${i}`}
                className={`relative aspect-square rounded-lg overflow-hidden ring-1 ${
                  p.promotable ? "ring-amber-400 ring-2" : "ring-slate-200"
                }`}
              >
                <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                {p.label.toLowerCase().includes("qr") && (
                  <span className="absolute bottom-0.5 left-0.5 right-0.5 text-[8px] text-white bg-black/60 rounded text-center px-0.5 py-0.5 font-bold">
                    QR
                  </span>
                )}
                {p.promotable && (
                  <span className="absolute top-0.5 right-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    !
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* PAY NOW CTA — most important */}
      {r.status === "pending" && hasPayableQr && (
        <div className="px-3 pb-2 space-y-1.5">
          {upiLink ? (
            <a
              href={upiLink}
              data-testid={`lm-fuel-pay-${r.id}`}
              className="block w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-sm font-bold text-center"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <QrCode className="h-4 w-4" /> Pay {fmtINR(r.total_amount)} via UPI App
              </span>
            </a>
          ) : (
            <button
              onClick={() => {
                // Open the first QR-looking photo in viewer for the LM to scan from their own UPI app
                const qrIdx = photoList.findIndex((p) =>
                  p.label.toLowerCase().includes("qr"));
                if (qrIdx >= 0) onView(photoList, qrIdx);
              }}
              data-testid={`lm-fuel-scan-${r.id}`}
              className="block w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white py-3 text-sm font-bold text-center"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <QrCode className="h-4 w-4" /> View QR to Scan &amp; Pay {fmtINR(r.total_amount)}
              </span>
            </button>
          )}
          {!stationUpiId && (
            <p className="text-[10px] text-slate-500 text-center">
              {stationQrPhoto || hasPumpQr
                ? "QR ko apne UPI app se scan karein → amount manual enter karein"
                : "No QR available — paise cash mein dein"}
            </p>
          )}
        </div>
      )}

      {/* Approve / Reject row */}
      {r.status === "pending" && (
        <div className="px-3 pb-3 grid grid-cols-2 gap-2">
          <button
            disabled={busy}
            onClick={onReject}
            data-testid={`lm-fuel-reject-${r.id}`}
            className="rounded-xl ring-1 ring-red-200 bg-red-50 text-red-700 py-2.5 text-xs font-bold disabled:opacity-40"
          >
            <span className="inline-flex items-center gap-1"><X className="h-3.5 w-3.5" /> Reject</span>
          </button>
          <button
            disabled={busy}
            onClick={onApprove}
            data-testid={`lm-fuel-approve-${r.id}`}
            className="rounded-xl bg-slate-900 text-white py-2.5 text-xs font-bold disabled:bg-slate-400"
          >
            <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Mark Paid &amp; Approve</span>
          </button>
        </div>
      )}

      {/* Approved info */}
      {r.status === "approved" && r.upi_txn_ref && (
        <div className="px-3 pb-3 text-[10px] text-slate-500 border-t border-slate-100 pt-2">
          UPI Ref: <span className="font-mono text-slate-700">{r.upi_txn_ref}</span>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-semibold ${
      map[status] || "bg-slate-100 text-slate-700"
    }`}>
      {status}
    </span>
  );
}

/* -------------------------------------------------------------------- */
/* Photo Viewer with swipe + QR promote                                   */
/* -------------------------------------------------------------------- */
function PhotoViewer({ viewer, onClose, onPromoteQR }) {
  const [idx, setIdx] = useState(viewer.idx || 0);
  const photos = viewer.photos || [];
  const current = photos[idx];
  if (!current) return null;

  const next = () => setIdx((i) => Math.min(i + 1, photos.length - 1));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex flex-col"
      onClick={onClose}
      data-testid="lm-fuel-photo-viewer"
    >
      <div
        className="flex items-center justify-between px-4 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-bold">{current.label}</div>
        <div className="text-[11px] text-slate-300">
          {idx + 1} / {photos.length}
        </div>
        <button onClick={onClose} data-testid="lm-fuel-viewer-close" className="p-1">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div
        className="flex-1 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.url}
          alt={current.label}
          className="max-w-full max-h-full object-contain rounded-lg bg-white"
        />
      </div>

      {/* Bottom toolbar */}
      <div
        className="px-4 py-3 flex items-center justify-between gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={prev}
          disabled={idx === 0}
          className="text-white/80 disabled:text-white/30 text-xs px-3 py-2 ring-1 ring-white/30 rounded-lg"
        >
          ← Prev
        </button>

        <div className="flex items-center gap-2">
          <a
            href={current.url}
            download
            onClick={(e) => e.stopPropagation()}
            className="text-white text-xs px-3 py-2 ring-1 ring-white/30 rounded-lg flex items-center gap-1"
            data-testid="lm-fuel-viewer-download"
          >
            <Download className="h-3.5 w-3.5" /> Save
          </a>
          {current.promotable && (
            <button
              onClick={(e) => { e.stopPropagation(); onPromoteQR(); }}
              data-testid="lm-fuel-viewer-promote"
              className="text-white bg-emerald-600 text-xs px-3 py-2 rounded-lg flex items-center gap-1 font-bold"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Set as Station QR
            </button>
          )}
        </div>

        <button
          onClick={next}
          disabled={idx === photos.length - 1}
          className="text-white/80 disabled:text-white/30 text-xs px-3 py-2 ring-1 ring-white/30 rounded-lg"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
